import { type PendingTwoFactorChallenge } from '@/lib/api/auth';
import { type UnauthorizedEventDetail } from '@/lib/utils/api-auth';

import type { AuthUser } from './AuthContext';

export const PENDING_TWO_FACTOR_STORAGE_KEY = 'zenopus.auth.pending-2fa';

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const getString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const getBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const getStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  return items.length > 0 ? items : undefined;
};

export function parseAuthUser(value: unknown): AuthUser | null {
  if (!isObject(value)) return null;

  const id = getString(value.id);
  const email = getString(value.email);
  const role = getString(value.role);
  if (!id || !email || !role) return null;

  return {
    id,
    email,
    role,
    name: typeof value.name === 'string' ? value.name : null,
    emailVerifiedAt: typeof value.emailVerifiedAt === 'string' ? value.emailVerifiedAt : null,
    hasPassword: getBoolean(value.hasPassword),
    twoFactorEnabled: getBoolean(value.twoFactorEnabled),
    twoFactorPendingSetup: getBoolean(value.twoFactorPendingSetup),
    authProviders: getStringArray(value.authProviders),
    mustReEnrollPasskey: getBoolean(value.mustReEnrollPasskey),
  };
}

export function readStoredPendingChallenge(): PendingTwoFactorChallenge | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(PENDING_TWO_FACTOR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingTwoFactorChallenge | null;
    if (!parsed || !getString(parsed.challengeToken)) {
      window.sessionStorage.removeItem(PENDING_TWO_FACTOR_STORAGE_KEY);
      return null;
    }
    return {
      challengeToken: parsed.challengeToken,
      expiresAt: parsed.expiresAt ?? null,
      pendingSetup: parsed.pendingSetup === true,
      email: parsed.email,
    };
  } catch {
    return null;
  }
}

export function buildAuthReturnTo(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search, hash } = window.location;
  const target = `${pathname}${search}${hash}`;
  return target.startsWith('/') ? target : '/';
}

// Endpoints for which a 401 is a normal part of the auth flow, not a sign the
// current session expired. Do not trigger a logout when these return 401.
export const UNAUTHORIZED_IGNORED_PATH_PREFIXES = [
  '/api/auth/me', // Probe endpoint; 401 means "not logged in" — expected on cold starts.
  '/api/auth/login', // Wrong password returns 401 — must not be treated as session expiry.
  '/api/auth/register', // Validation errors may 401 in some flows.
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/password-setup',
  '/api/auth/two-factor', // 2FA challenge + verify + setup/confirm/disable.
  '/api/auth/social', // OAuth start/callback flows.
  '/api/auth/logout', // Already logging out.
  // Step-up / sudo-mode (M4) re-auth. The GET methods-probe returns 401
  // `auth_required` and the POST verify returns 401 `invalid_credentials`
  // / `two_factor_invalid` / `uv_reauth_required` on a WRONG sudo
  // credential (server/internal/httpapi/routes/auth_sudo.go:173,382-388).
  // These are in-flow re-auth failures the SudoChallengeModal surfaces
  // inline — they must NOT be treated as session expiry, otherwise a
  // mistyped sudo password logs the still-authenticated user out and
  // redirects to /auth instead of letting them retry in the modal.
  '/api/auth/sudo',
  // Self-service profile / email / password change: a 401 here is "wrong
  // current password" (proof-in-body fail), NOT a session-expiry. The
  // server returns 400 now (see user_profile.go), but a stray 401 from
  // legacy clients or middleware races must still not auto-log-out the
  // user mid-settings — they'd lose their unsaved form state and the
  // `/auth` redirect collides with the [project_id] dynamic route
  // (project_id='auth' → broken "project auth" chat view).
  '/api/user/profile',
];

// Client-side routes where a 401 should not trigger a redirect (we're already
// in an unauthenticated/auth-flow context).
export const UNAUTHORIZED_IGNORED_PATHNAMES = ['/auth/challenge', '/auth', '/reset-password', '/set-password', '/sso'];

export const VISIBILITY_REFRESH_STALE_MS = 5 * 60 * 1000; // 5 minutes

export function pathnameFromUrl(url: string): string {
  if (!url) return '';
  // Handle absolute URLs and relative paths uniformly.
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(url, origin).pathname;
  } catch {
    // Fall back to best-effort extraction.
    const queryIndex = url.indexOf('?');
    return queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  }
}

export function shouldIgnoreUnauthorized(detail: UnauthorizedEventDetail | undefined): boolean {
  const requestPath = pathnameFromUrl(detail?.url ?? '');
  if (requestPath && UNAUTHORIZED_IGNORED_PATH_PREFIXES.some((prefix) => requestPath.startsWith(prefix))) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    if (UNAUTHORIZED_IGNORED_PATHNAMES.some((prefix) => currentPath.includes(prefix))) {
      return true;
    }
  }
  return false;
}
